/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, type ReactNode, type Ref } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { type StrictXCSSProp } from '@atlaskit/css';

// Placing this const below the function breaks Compiled!
const styles = cssMap({
	root: {
		margin: '0',
		padding: '0',
		/**
		 * The CSS reset has higher precedence, and unfortunately there is no
		 * good / UI Styling Standard compliant way to override it.
		 *
		 * A 'correct' solution would be using `:where()` pseudos in the global
		 * reset so that reset styles have 0 specificity, and will never override
		 * styles that have been explicitly set.
		 */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		marginTop: '0 !important',
	},
});

type ListProps = {
	children: ReactNode;
	xcss?: StrictXCSSProp<'alignItems' | 'display' | 'gap', never>;
};

function _List({ children, xcss }: ListProps, ref: Ref<HTMLUListElement>) {
	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
		<ul css={styles.root} ref={ref} className={xcss}>
			{children}
		</ul>
	);
}

/**
 * __List__
 *
 * An [unordered list](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/ul) with visual styles removed,
 * for semantically grouping list items.
 */
export const List = forwardRef<HTMLUListElement, ListProps>(_List);
